import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, Printer, RefreshCw, ChevronDown, ChevronUp, ExternalLink, ShieldCheck, Users, Wrench, FileText, AlertTriangle, ListChecks } from 'lucide-react';
import toast from 'react-hot-toast';
import { processOptimizationAPI, ExecutiveStory as ExecutiveStoryData, StoryFigure, StoryCitation } from '../../services/processOptimizationAPI';

interface ExecutiveStoryProps {
  projectId: number;
  /** Jump to the opportunity card (drill-through from a citation or a decision). */
  onOpenOpportunity?: (opportunityId: number) => void;
  /** Bumped by the parent after a regenerate / disposition change so the story reloads. */
  refreshKey?: number;
}

const DISPOSITION_STYLE: Record<string, string> = {
  automate: 'bg-blue-100 text-blue-800',
  skill_gpt: 'bg-purple-100 text-purple-800',
  keep_human: 'bg-amber-100 text-amber-800',
  do_not_change: 'bg-gray-200 text-gray-700',
};

const DISPOSITION_SHORT: Record<string, string> = {
  automate: 'Automate',
  skill_gpt: 'Skill / GPT',
  keep_human: 'Keep human',
  do_not_change: 'Do not change',
};

const RULE_LABEL: Record<string, string> = {
  sod_conflict: 'Segregation of duties',
  doi_regulatory_interface: 'Regulator interface',
  write_access_no_approval: 'Write access without approval',
  high_liability_judgment: 'Discretionary judgment / liability',
  mrm_restricted: 'Model-risk gated',
  insufficient_evidence: 'Insufficient evidence',
  analytical_synthesis: 'Analytical work',
  document_drafting: 'Document drafting',
  exception_variable_input: 'Variable exceptions',
  compliance_interpretation: 'Compliance interpretation',
  approval_chain: 'Approval chain',
  customer_facing: 'Customer-facing',
  default_automate: 'Structured, repeatable',
};

const usd = (v: number | null | undefined) => (v === null || v === undefined ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v));
const numFmt = (v: number | null | undefined, unit?: string | null) => {
  if (v === null || v === undefined) return '—';
  const n = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(v);
  if (unit === 'usd' || unit === 'usd_per_year') return usd(v);
  if (unit === 'hours' || unit === 'hours_per_year') return `${n} h`;
  return n;
};
const secToMin = (s: number | null) => (s === null ? '' : `${Math.round(s / 60)} min`);

/** A figure with its provenance available on hover — the "every number traces" contract made visible. */
const Figure: React.FC<{ f: StoryFigure; label: string; tone?: string }> = ({ f, label, tone = 'text-gray-900' }) => (
  <div className="bg-white border border-gray-200 rounded-lg px-4 py-3" title={`Source: ${f.source}`}>
    <p className={`text-2xl font-bold ${tone}`}>{numFmt(f.value, f.unit)}</p>
    <p className="text-[11px] text-gray-500 mt-0.5 leading-snug">{label}</p>
  </div>
);

const Section: React.FC<{ n: number; title: string; icon: React.ReactNode; children: React.ReactNode; subtitle?: string }> = ({ n, title, icon, children, subtitle }) => (
  <section className="exec-section break-inside-avoid">
    <div className="flex items-center gap-2 mb-2">
      <span className="w-6 h-6 rounded-full bg-gray-900 text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0">{n}</span>
      <span className="text-gray-500">{icon}</span>
      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">{title}</h3>
      {subtitle && <span className="text-[11px] text-gray-400 ml-1">{subtitle}</span>}
    </div>
    <div className="pl-8">{children}</div>
  </section>
);

const ExecutiveStory: React.FC<ExecutiveStoryProps> = ({ projectId, onOpenOpportunity, refreshKey = 0 }) => {
  const [story, setStory] = useState<ExecutiveStoryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openCitation, setOpenCitation] = useState<string | null>(null);
  const [showAllCitations, setShowAllCitations] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await processOptimizationAPI.getExecutiveStory(projectId);
      if (res.success && res.data) setStory(res.data);
      else setStory(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load executive story');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [projectId, refreshKey]);

  const totals = useMemo(() => {
    if (!story) return null;
    const c = story.dispositions.counts;
    const changing = (c.automate.value || 0) + (c.skill_gpt.value || 0);
    const protectedCount = (c.keep_human.value || 0) + (c.do_not_change.value || 0);
    return { changing, protectedCount };
  }, [story]);

  const handlePrint = () => {
    // Print stylesheet (below) isolates the story: a 3-page exec pack straight from the browser's PDF printer.
    document.body.classList.add('exec-story-printing');
    const cleanup = () => document.body.classList.remove('exec-story-printing');
    window.addEventListener('afterprint', cleanup, { once: true });
    setTimeout(() => { window.print(); setTimeout(cleanup, 2000); }, 50);
  };

  if (loading) {
    return <div className="text-sm text-gray-500 flex items-center gap-2"><RefreshCw className="w-4 h-4 animate-spin" /> Assembling the executive story from the captured data…</div>;
  }
  if (error) {
    return <div className="text-sm text-red-600 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {error} <button onClick={load} className="underline">retry</button></div>;
  }
  if (!story) {
    return <div className="text-sm text-gray-500">No assessment yet — run the assessment in Step 1 to build the story.</div>;
  }

  const s = story;
  const proj = s.situation.project;
  const grounded = s.benefits.grounded;
  const citationsFlat = s.citations.flatMap(o => o.citations.map(c => ({ ...c, opportunity_id: o.opportunity_id, opportunity_title: o.title, disposition: o.disposition })));
  const visibleCitations = showAllCitations ? citationsFlat : citationsFlat.slice(0, 8);

  return (
    <div id="executive-story" className="space-y-6">
      <style>{`
        @media print {
          body.exec-story-printing * { visibility: hidden !important; }
          body.exec-story-printing #executive-story, body.exec-story-printing #executive-story * { visibility: visible !important; }
          body.exec-story-printing #executive-story { position: absolute !important; left: 0; top: 0; width: 100%; padding: 0 12mm; }
          body.exec-story-printing .no-print { display: none !important; }
          .exec-section { page-break-inside: avoid; }
          .exec-page-break { page-break-before: always; }
          @page { size: A4; margin: 14mm; }
        }
      `}</style>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-gray-400">Executive story</p>
          <h2 className="text-xl font-bold text-gray-900">{proj.name}</h2>
          <p className="text-xs text-gray-500 mt-1">
            {proj.client_name ? `${proj.client_name} · ` : ''}
            Assessed {s.situation.assessed_at ? new Date(s.situation.assessed_at).toLocaleDateString() : '—'}
            {s.situation.model_id ? ` · model ${s.situation.model_id}` : ''}
            {s.situation.engine_version ? ` · engine v${s.situation.engine_version}` : ''}
            {s.situation.inputs_hash ? ` · inputs ${s.situation.inputs_hash.slice(0, 10)}…` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 no-print">
          <button onClick={load} className="p-2 text-gray-400 hover:text-gray-700" title="Reload from records"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={handlePrint} className="flex items-center gap-1.5 px-3 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-gray-800">
            <Printer className="w-4 h-4" /> Export exec pack (PDF)
          </button>
        </div>
      </div>

      {/* Throughline — the one paragraph an exec retells; every number is a count. */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 text-sm text-gray-800 leading-relaxed" title={s.integrity.rule}>
        We reviewed <strong>{s.situation.steps_analyzed.value}</strong> process steps in <strong>{proj.name}</strong>
        {s.situation.sources_used.value ? <> using <strong>{s.situation.sources_used.value}</strong> source{s.situation.sources_used.value === 1 ? '' : 's'}{s.situation.recordings_used.value ? <> ({s.situation.recordings_used.value} recording{s.situation.recordings_used.value === 1 ? '' : 's'})</> : null}</> : null}
        {' '}and found <strong>{s.situation.opportunities.value}</strong> opportunities, each tied to <strong>{s.evidence.total_citations.value}</strong> cited moments in the evidence.
        {' '}We will change <strong>{totals?.changing}</strong> of them ({s.dispositions.counts.automate.value} automated, {s.dispositions.counts.skill_gpt.value} as team skills)
        {' '}and deliberately keep AI out of <strong>{totals?.protectedCount}</strong> ({s.dispositions.counts.keep_human.value} with a human in the loop, {s.dispositions.counts.do_not_change.value} compliance-protected).
        {grounded ? <> Grounded on imported metrics, the changing opportunities are worth <strong>{usd(grounded.net_annual_savings.value)}</strong> a year net across {s.benefits.grounded_opportunities.value} grounded opportunit{s.benefits.grounded_opportunities.value === 1 ? 'y' : 'ies'}.</> : <> Savings are still AI estimates for {s.benefits.estimated_opportunities.value} opportunit{s.benefits.estimated_opportunities.value === 1 ? 'y' : 'ies'}; import metrics to ground them.</>}
        {' '}<strong>{s.decisions.count.value}</strong> decision{s.decisions.count.value === 1 ? '' : 's'} need{s.decisions.count.value === 1 ? 's' : ''} leadership sign-off.
      </div>

      {/* 1. Situation */}
      <Section n={1} title="Situation" icon={<BookOpen className="w-4 h-4" />} subtitle="what we looked at">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Figure f={s.situation.steps_analyzed} label="process steps analyzed" />
          <Figure f={s.situation.opportunities} label="opportunities identified" />
          <Figure f={s.situation.sources_used} label="source documents / recordings cited" />
          <Figure f={s.evidence.total_citations} label="citations to source moments" />
        </div>
        {s.situation.derived_from_evidence && <p className="text-[11px] text-amber-700 mt-2">Steps were derived directly from the ingested recording — no process map existed yet.</p>}
        {s.situation.pattern_source && s.situation.pattern_source !== 'ai' && <p className="text-[11px] text-red-700 mt-2">Assessment ran in rule-based fallback mode (no AI citations). Re-run when the model is available.</p>}
      </Section>

      {/* 2. Evidence */}
      <Section n={2} title="Evidence" icon={<FileText className="w-4 h-4" />} subtitle="where the findings come from">
        <div className="grid md:grid-cols-2 gap-4">
          <ul className="space-y-1.5">
            {s.evidence.sources.map(src => (
              <li key={src.document_id} className="text-sm text-gray-800 flex items-center gap-2">
                <span aria-hidden="true">{src.media_type === 'video' ? '🎬' : src.media_type === 'audio' ? '🎧' : '📄'}</span>
                <span className="truncate" title={src.filename}>{src.filename}</span>
                {src.media_duration_sec !== null && <span className="text-[11px] text-gray-400">{secToMin(src.media_duration_sec)}</span>}
                <span className="ml-auto text-[11px] text-gray-500 whitespace-nowrap">{src.citation_count} citation{src.citation_count === 1 ? '' : 's'}</span>
              </li>
            ))}
            {s.evidence.sources.length === 0 && <li className="text-sm text-gray-400">No citations recorded on this assessment.</li>}
          </ul>
          <div className="text-sm text-gray-700" title={s.evidence.status_counts.source}>
            <p><strong>{s.evidence.opportunities_with_citations.value}</strong> of {s.situation.opportunities.value} opportunities are cited.</p>
            {(s.evidence.status_counts.verified + s.evidence.status_counts.likely + s.evidence.status_counts.unverified) > 0 ? (
              <p className="mt-1">Quote checks: <span className="text-emerald-700 font-semibold">{s.evidence.status_counts.verified} verified</span>, <span className="text-amber-700 font-semibold">{s.evidence.status_counts.likely} likely</span>, <span className="text-red-700 font-semibold">{s.evidence.status_counts.unverified} unverified</span>.</p>
            ) : (
              <p className="mt-1 text-gray-400">Quote checks were not recorded for this assessment — regenerate to verify each citation against its source.</p>
            )}
            {(s.evidence.opportunities_without_citations.value || 0) > 0 && <p className="mt-1 text-red-700">{s.evidence.opportunities_without_citations.value} opportunit{s.evidence.opportunities_without_citations.value === 1 ? 'y has' : 'ies have'} no evidence and cannot ship as skills.</p>}
          </div>
        </div>
      </Section>

      {/* 3. Dispositions */}
      <Section n={3} title="What we will and will not change" icon={<ShieldCheck className="w-4 h-4" />} subtitle="deterministic engine, human overrides recorded">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <Figure f={s.dispositions.counts.automate} label="automate (approved tool)" tone="text-blue-700" />
          <Figure f={s.dispositions.counts.skill_gpt} label="Claude Skill / Custom GPT" tone="text-purple-700" />
          <Figure f={s.dispositions.counts.keep_human} label="keep a human in the loop" tone="text-amber-700" />
          <Figure f={s.dispositions.counts.do_not_change} label="do not change (protected)" tone="text-gray-700" />
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1.5">We will change</p>
            <ul className="space-y-1">
              {s.dispositions.will_change.map(o => (
                <li key={o.id} className="text-sm text-gray-800">
                  <div className="flex items-start gap-2">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded mt-0.5 whitespace-nowrap ${DISPOSITION_STYLE[o.disposition]}`}>{DISPOSITION_SHORT[o.disposition]}</span>
                    {o.pending_approval && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded mt-0.5 whitespace-nowrap bg-amber-100 text-amber-800" title={o.pending_approval_reason || ''}>Pending Approval</span>
                    )}
                    <button onClick={() => onOpenOpportunity?.(o.id)} className="text-left hover:underline">{o.title}</button>
                    {o.human_override && <span className="text-[10px] text-amber-700 whitespace-nowrap" title={o.human_override.reason || ''}>human override</span>}
                  </div>
                  {o.pending_approval && o.pending_approval_reason && (
                    <p className="text-[11px] text-amber-700 pl-1 mt-0.5">{o.pending_approval_reason}</p>
                  )}
                </li>
              ))}
              {s.dispositions.will_change.length === 0 && <li className="text-sm text-gray-400">Nothing is slated for automation yet.</li>}
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-700 mb-1.5">Where we will not use AI <span className="font-normal text-gray-400">(and why)</span></p>
            <ul className="space-y-1">
              {s.dispositions.will_not_use_ai.map(o => (
                <li key={o.id} className="text-sm text-gray-800">
                  <div className="flex items-start gap-2">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded mt-0.5 whitespace-nowrap ${DISPOSITION_STYLE[o.disposition]}`}>{DISPOSITION_SHORT[o.disposition]}</span>
                    <button onClick={() => onOpenOpportunity?.(o.id)} className="text-left hover:underline">{o.title}</button>
                  </div>
                  <p className="text-[11px] text-gray-500 pl-1 mt-0.5">
                    {o.human_override
                      ? <>Human override{o.human_override.by ? ` by ${o.human_override.by}` : ''}{o.rule ? ` (engine said ${RULE_LABEL[o.rule] || o.rule} → ${DISPOSITION_SHORT[o.human_override.from] || o.human_override.from})` : ''}: {o.human_override.reason || 'no reason given'}</>
                      : <>{o.rule ? `${RULE_LABEL[o.rule] || o.rule} — ` : ''}{o.rationale}</>}
                  </p>
                  {o.kept_with && o.kept_with.length > 0 && (
                    <p className="text-[11px] text-emerald-700 pl-1 mt-0.5">Judgment stays with: {o.kept_with.join(', ')}</p>
                  )}
                </li>
              ))}
              {s.dispositions.will_not_use_ai.length === 0 && <li className="text-sm text-gray-400">No protected steps identified.</li>}
            </ul>
          </div>
        </div>
      </Section>

      <div className="exec-page-break" />

      {/* 4. Artifacts */}
      <Section n={4} title="What the team gets" icon={<Wrench className="w-4 h-4" />} subtitle="artifacts produced">
        <div className="grid grid-cols-3 gap-3 mb-3">
          <Figure f={s.artifacts.skill_bundles} label="skill bundles generated (SKILL.md, GPT spec, checks)" />
          <Figure f={s.artifacts.workflow_blueprints} label="workflow blueprints" />
          <Figure f={s.artifacts.skill_bundles_refused} label="refused — no evidence (fail closed)" tone="text-red-700" />
        </div>
        {s.artifacts.items.length > 0 && (
          <ul className="text-sm text-gray-800 space-y-1">
            {s.artifacts.items.map(a => <li key={a.id}>• {a.title} <span className="text-[11px] text-gray-400">— {DISPOSITION_SHORT[a.disposition]}, {a.citation_count} citations, {new Date(a.generated_at).toLocaleDateString()}</span></li>)}
          </ul>
        )}
        {s.artifacts.pending.length > 0 && <p className="text-[11px] text-gray-500 mt-2">{s.artifacts.pending.length} approved-to-change opportunit{s.artifacts.pending.length === 1 ? 'y has' : 'ies have'} no artifact yet.</p>}
      </Section>

      {/* 5. Benefits */}
      <Section n={5} title="Benefits" icon={<ListChecks className="w-4 h-4" />} subtitle="grounded engine sums only — estimates are labelled, never totalled">
        {grounded ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
              <Figure f={grounded.net_annual_savings} label="net annual savings" tone="text-emerald-700" />
              <Figure f={grounded.total_annual_benefit} label="gross annual benefit" />
              <Figure f={grounded.annual_hours_saved} label="hours saved / year" />
              <Figure f={grounded.total_implementation_cost} label="implementation cost" />
              <Figure f={grounded.npv_3year} label="3-year NPV" />
            </div>
            <table className="w-full text-xs text-gray-700 mb-2">
              <thead><tr className="text-left text-gray-400"><th className="py-1">Grounded opportunity</th><th className="text-right">Net / yr</th><th className="text-right">NPV 3y</th><th className="text-right">Payback</th></tr></thead>
              <tbody>
                {grounded.per_opportunity.map(o => (
                  <tr key={o.id} className="border-t border-gray-100"><td className="py-1"><button onClick={() => onOpenOpportunity?.(o.id)} className="hover:underline text-left">{o.title}</button></td><td className="text-right">{usd(o.annual_savings)}</td><td className="text-right">{usd(o.npv_3year)}</td><td className="text-right">{o.payback_months === null ? '—' : `${o.payback_months} mo`}</td></tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-gray-400">Grounded calculation v{grounded.calculation_version} on imported labor rates and process baselines. {s.benefits.estimated_opportunities.value} opportunit{s.benefits.estimated_opportunities.value === 1 ? 'y remains' : 'ies remain'} unquantified.</p>
          </>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
            No grounded financials yet: all {s.benefits.estimated_opportunities.value} opportunities carry AI estimates only. Import process metrics (Step 4 / Measure) to compute savings, NPV and payback deterministically.
          </div>
        )}
        {s.benefits.rollup && s.benefits.rollup.total_records > 0 && (
          <div className="mt-3" title={s.benefits.rollup.source}>
            <p className="text-xs font-semibold text-gray-700 mb-1">Projected vs realized</p>
            <ul className="text-xs text-gray-700 space-y-0.5">
              {s.benefits.rollup.by_type.flatMap(t => t.metrics.map(m => (
                <li key={`${t.benefit_type}-${m.metric_key}`}>{t.benefit_type.replace(/_/g, ' ')} · {m.label}: projected {numFmt(m.projected, m.unit)}{m.realized !== null ? `, realized ${numFmt(m.realized, m.unit)}` : ', not yet realized'}</li>
              )))}
            </ul>
          </div>
        )}
        {s.benefits.estimates.length > 0 && (
          <details className="mt-3 no-print">
            <summary className="text-[11px] text-gray-500 cursor-pointer">AI estimates (per opportunity, not summed)</summary>
            <ul className="text-[11px] text-gray-500 mt-1 space-y-0.5">{s.benefits.estimates.map(e => <li key={e.id}>• {e.title}: {e.estimated_time_reduction}</li>)}</ul>
          </details>
        )}
      </Section>

      {/* 6. Controls */}
      <Section n={6} title="Controls touched by automation" icon={<ShieldCheck className="w-4 h-4" />} subtitle="and the approvals that gate it">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
          <Figure f={s.controls.distinct_sox_controls} label="distinct SOX controls impacted" />
          <Figure f={s.controls.control_activities} label="control activities required" />
          <Figure f={s.controls.opportunities_touching_controls} label="changing opportunities touching controls" />
          <Figure f={s.controls.approvals.mrm.pending} label="MRM approvals pending" tone="text-amber-700" />
          <Figure f={s.controls.approvals.sod.pending} label="SOD sign-offs pending" tone="text-amber-700" />
        </div>
        {s.controls.by_opportunity.length > 0 && (
          <ul className="text-xs text-gray-700 space-y-1">
            {s.controls.by_opportunity.map(c => (
              <li key={c.id}>• <span className="font-medium">{c.title}</span>: {c.sox_controls.map(x => x.id).filter(Boolean).join(', ') || 'no SOX ids'}{c.control_activities.length ? ` · ${c.control_activities.length} control activit${c.control_activities.length === 1 ? 'y' : 'ies'}` : ''}</li>
            ))}
          </ul>
        )}
      </Section>

      {/* 7. Change / people */}
      <Section n={7} title="People and change" icon={<Users className="w-4 h-4" />} subtitle="augmentation, not replacement — what happens to each role">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
          <Figure f={s.change.stakeholders_involved} label="stakeholders involved" />
          <Figure f={s.change.approvers_required} label="approvers required" />
          <Figure f={s.change.stakeholder_effort_hours} label="stakeholder effort" />
          <Figure f={s.change.training_hours} label="training hours" />
          <Figure f={s.change.high_risk_flags} label="resistance risks (high + critical)" tone="text-red-700" />
        </div>
        {(s.change.job_stories || []).length > 0 && (
          <div className="mb-4 space-y-2">
            <p className="text-xs font-semibold text-gray-700">What happens to my job — per opportunity, in plain language</p>
            {(s.change.job_stories || []).map(js => (
              <div key={js.opportunity_id} className={`border rounded-lg px-3 py-2 ${js.disposition === 'keep_human' || js.disposition === 'do_not_change' ? 'border-emerald-200 bg-emerald-50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-start gap-2">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded mt-0.5 whitespace-nowrap ${DISPOSITION_STYLE[js.disposition] || 'bg-gray-100 text-gray-600'}`}>{DISPOSITION_SHORT[js.disposition] || js.disposition}</span>
                  <div className="min-w-0">
                    <button onClick={() => onOpenOpportunity?.(js.opportunity_id)} className="text-xs font-medium text-gray-800 text-left hover:underline">{js.title}</button>
                    {js.plain_language && <p className="text-[11px] text-gray-600 mt-0.5">{js.plain_language}</p>}
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      {js.named_owner ? <>Owner: <span className="font-medium text-gray-700">{js.named_owner}</span></> : 'Owner: unassigned'}
                      {js.roles_affected.length > 0 && <> · Roles: {js.roles_affected.join(', ')}</>}
                    </p>
                    {js.upskilling_path.length > 0 && (
                      <p className="text-[11px] text-blue-700 mt-0.5">Upskilling: {js.upskilling_path.join(' · ')}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-4 text-xs text-gray-700">
          <ul className="space-y-0.5">
            {s.change.stakeholders.slice(0, 8).map(st => <li key={`${st.name}-${st.department}`}>• {st.name}{st.department ? ` (${st.department})` : ''} — {st.opportunities} opportunit{st.opportunities === 1 ? 'y' : 'ies'}{st.approval_required ? ', approver' : ''}</li>)}
          </ul>
          <ul className="space-y-0.5">
            {(() => {
              // Same flag raised on several opportunities reads as one risk with a count, not a repeated line.
              const grouped = new Map<string, { title: string; mitigation: string | null; count: number; groups: Set<string> }>();
              s.change.risk_flags.filter(f => f.severity === 'high' || f.severity === 'critical').forEach(f => {
                const key = f.title || f.type || 'risk';
                const g = grouped.get(key);
                if (g) { g.count += 1; (f.affected_groups || []).forEach(x => g.groups.add(x)); }
                else grouped.set(key, { title: key, mitigation: f.mitigation, count: 1, groups: new Set(f.affected_groups || []) });
              });
              return Array.from(grouped.values()).slice(0, 6).map(g => (
                <li key={g.title}>⚠ {g.title}{g.count > 1 ? ` (×${g.count} opportunities)` : ''}{g.groups.size > 0 ? ` — roles: ${Array.from(g.groups).join(', ')}` : ''}{g.mitigation ? ` — ${g.mitigation}` : ''}</li>
              ));
            })()}
          </ul>
        </div>
      </Section>

      {/* 8. Decisions needed */}
      <Section n={8} title="Decisions needed" icon={<AlertTriangle className="w-4 h-4" />} subtitle="derived from record states">
        {s.decisions.items.length === 0 ? (
          <p className="text-sm text-gray-500">Nothing outstanding — every changing opportunity is approved, grounded, cited and has an artifact.</p>
        ) : (
          <ol className="space-y-1.5">
            {s.decisions.items.map((d, i) => (
              <li key={i} className="text-sm text-gray-800 flex items-start gap-2" title={d.source}>
                <span className="font-bold text-gray-400">{i + 1}.</span>
                <span>
                  {d.title}
                  <span className="no-print"> {d.opportunity_ids.slice(0, 5).map(id => <button key={id} onClick={() => onOpenOpportunity?.(id)} className="text-[10px] text-blue-600 hover:underline ml-1">#{id}</button>)}</span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </Section>

      <div className="exec-page-break" />

      {/* 9. Citations — drill-through */}
      <Section n={9} title="Citations" icon={<FileText className="w-4 h-4" />} subtitle="click to open the quote at its timestamp or page">
        <ul className="space-y-1">
          {visibleCitations.map(c => {
            const key = `${c.opportunity_id}-${c.evidence_id}`;
            const open = openCitation === key;
            return (
              <li key={key} className="text-xs text-gray-800">
                <button onClick={() => setOpenCitation(open ? null : key)} className="w-full text-left flex items-center gap-2 hover:bg-gray-50 rounded px-1 py-0.5">
                  <span aria-hidden="true">{c.media_type === 'video' ? '🎬' : c.media_type === 'audio' ? '🎧' : '📄'}</span>
                  <span className="font-medium truncate">{c.filename}</span>
                  <span className="text-gray-500 whitespace-nowrap">{c.locator || 'text'}</span>
                  {c.status && <span className={`text-[9px] font-semibold uppercase px-1 rounded ${c.status === 'verified' ? 'bg-emerald-100 text-emerald-700' : c.status === 'unverified' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{c.status}</span>}
                  <span className="ml-auto text-gray-400 truncate max-w-[40%]">{c.opportunity_title}</span>
                  {open ? <ChevronUp className="w-3 h-3 text-gray-400 no-print" /> : <ChevronDown className="w-3 h-3 text-gray-400 no-print" />}
                </button>
                {open && (
                  <div className="ml-6 mt-1 mb-2 border border-indigo-200 bg-indigo-50 rounded p-2">
                    {c.quote && <p className="italic text-gray-800">“{c.quote}”</p>}
                    {c.reason && <p className="text-indigo-800 mt-1"><span className="font-semibold">Why:</span> {c.reason}</p>}
                    <button onClick={() => onOpenOpportunity?.(c.opportunity_id)} className="no-print mt-1 text-[11px] text-blue-600 hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" /> open opportunity #{c.opportunity_id}</button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        {citationsFlat.length > 8 && (
          <button onClick={() => setShowAllCitations(v => !v)} className="no-print text-[11px] text-blue-600 hover:underline mt-2">{showAllCitations ? 'Show fewer' : `Show all ${citationsFlat.length} citations`}</button>
        )}
      </Section>

      <p className="text-[10px] text-gray-400 border-t border-gray-100 pt-2">{s.integrity.rule} Opportunity records: {s.integrity.opportunity_count}{s.integrity.inputs_hash ? ` · assessment inputs ${s.integrity.inputs_hash.slice(0, 16)}…` : ''} · story v{s.version} · generated {new Date(s.generated_at).toLocaleString()}</p>
    </div>
  );
};

export default ExecutiveStory;
